import { isClonedVoiceCode } from '@vbee-holding/aivoice-shared-lib';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { Checkbox, Typography } from '@mui/material';
import { WarningRounded as WarningRoundedIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { OrderedSet } from 'immutable';
import { BREAK_LINE_DELIMITER } from '@src/constants';
import {
  CONVERTING_STATUS,
  EXTRACTION_STATUS,
  MAX_CHARACTER_PER_BLOCK,
} from '@src/constants/project';
import { ContentState, Editor, EditorState, Modifier } from 'draft-js';
import sanitize from 'sanitize-html';
import Tooltip from '@src/components/Tooltip';
import { COLOR } from '@src/styles/color';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import {
  BREAK_TIME_REGEX,
  DEFAULT_BREAK_TIME,
  DEFAULT_SPEED,
  VOICE_OWNERSHIP,
} from '@src/constants/voice';
import { countTextLength } from '@src/services/tts';
import BreakTimeHighlight from '@src/containers/TTS/BreakTimeHightLight';
import BlackWordHighLight from '@src/containers/TTS/BlackWordHighLight';
import {
  generateDecorator,
  generateCompositeDecorator,
} from '@src/services/editor';
import { checkBrowser } from '@src/utils/browser';
import { MAX_CHARACTER_LENGTH, VALID_LETTERS } from '@src/constants/tts';
import actions from '@src/redux/actions';
import {
  downloadFileByUrl,
  // downloadFileUsingUrl,
  getFileName,
} from '@src/utils/file';
import { LANDING_PAGE_URL } from '@src/configs';
import { CATEGORY_TYPE, PLATFORM_TYPE } from '@src/constants/support';
import { getPresignedAudioUrl } from '@src/apis/requests';
import { trackBlockAudioDownloaded } from '@src/services/moengage';
import {
  getBlockStatus,
  isBlockChanged,
  isNoteChanged,
  hasNarrationConfig,
  formatInvalidTexts,
  checkBlockTextError,
  isBlockTextSkeleton,
} from '@src/services/project';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import useIsAutoSaveOverLimit from '@src/hooks/useIsAutoSaveOverLimit';
import VoiceSelectionDialog from '@src/containers/VoiceSelectionDialog';
import NewTypography from '@src/new-components/Typography';
import {
  StyledBlockEditor,
  StyledContentBlock,
  StyledContentBlockWrapper,
  StyledInfoBlock,
  StyledOrdinalNumberBlock,
} from './index.style';
import NoteChangedWarningPopover from './NoteChangedWarningPopover';
import MoreOptionBlock from '../../component/MoreOptionBlock';
import ConfigOptionBlock from '../../component/ConfigOptionBlock';
import CreditsCount from '../../component/CreditsCount';
import EditNameBlock from './EditNameBlock';
import NewSynthesisButton from '../../component/NewSynthesisButton';
import {
  TextLinesSkeleton,
  SlideThumbnailSkeleton,
  ConfigToolbarSkeleton,
  BlockActionsSkeleton,
} from './ExtractSkeleton';

const ContentBlock = ({
  blockId,
  block,
  isLastBlock,
  isFocusing,
  onFocusingBlock,
  onSaveProject,
  voiceDefault,
  isPlayingBlock,
  isDownloading,
  onOpenOutOfCreditsDialog,
  onOpenWrongSyntaxDialog,
  onOpenWrongSyntaxDialogV2,
  onOpenDiscardedVoiceCloningDialog,
  checkValidCreateSynthesis,
  isHighlight,
  handlePlayAudio,
  projectId,
  originalBlock,
  onConvertBlockToAudio,
  audioType,
  enableCreditUsagePreview,
  onReSynthesizeBlock,
  blockAudioCurrentTime,
  blockAudioDuration,
  handleSeekedInBlock,
  currentPlayingBlockId,
  foundBlackWords = [],
  onOpenAIGenerateDialog,
}) => {
  const { t, i18n } = useTranslation();

  const { language } = i18n;
  const inputRef = useRef(null);
  const blockRef = useRef(null);
  const toolbarWrapperRef = useRef(null);
  const userId = useSelector((state) => state.auth.user?.id);
  const userEmail = useSelector((state) => state.auth.user?.email);
  const userPhoneNumber = useSelector((state) => state.auth.user?.phoneNumber);

  const blockIds = useSelector((state) => state.project.multiBlock?.blockIds);
  const source = useSelector((state) => state.project.source);
  const isGeneratingNarration = useSelector(
    (state) => state.project.isGeneratingNarration,
  );
  const generatingBlockIds = useSelector(
    (state) => state.project.generatingBlockIds,
  );
  const { getFeatureValue } = useFeatureFlags();
  const featureFlagUser = useMemo(
    () => ({ userId, email: userEmail, phoneNumber: userPhoneNumber }),
    [userId, userEmail, userPhoneNumber],
  );
  const slideV2Enabled = getFeatureValue(
    FEATURE_KEYS.PRESENTATION_SLIDE_V2,
    featureFlagUser,
  );
  // Skeleton UI is shown while the source is still being extracted (PENDING) and
  // the PRESENTATION_EXTRACT_PROGRESS feature flag is enabled.
  const extractProgressEnabled = getFeatureValue(
    FEATURE_KEYS.PRESENTATION_EXTRACT_PROGRESS,
    featureFlagUser,
  );
  const isSkeletonUI =
    extractProgressEnabled &&
    source?.extraction?.response?.status === EXTRACTION_STATUS.PENDING;
  const noteChangedBlock = useSelector(
    (state) => state.project.multiBlock?.blocks?.[blockId],
  );

  const indexBlock = useMemo(() => {
    const idx = blockIds.indexOf(blockId) + 1;
    return idx;
  }, [blockIds, blockId]);

  const { filterVoice } = useSelector((state) => state.voice);
  const dispatch = useDispatch();

  const {
    voiceCode = voiceDefault?.code,
    text = '',
    speed = DEFAULT_SPEED,
    breakTime = DEFAULT_BREAK_TIME,
    audioLink,
    status,
    requestId,
    showSuccessIcon,
    audioExpiredAt,
    voice = voiceDefault,
    isBlockEditting,
    title = '',
  } = block;

  // Seperately destructuring hasEmphasis and emphasis since hasEmphasis is dependent on voice?.hasEmphasis
  const { hasEmphasis = voice?.hasEmphasis, emphasis = 0 } = block;

  const maxCharLimit = MAX_CHARACTER_PER_BLOCK;

  const blockStatus = getBlockStatus({
    block,
    originalBlock,
    audioType,
    maxCharLimit,
    blackWordsList: foundBlackWords,
    voice,
  });

  const {
    exceedMaxChar: exceedBlockMaxChar,
    invalidCharacterLengthTexts,
    hasInvalidCharacterLength,
    hasInvalidCharacters,
    hasBlackWords,
  } = checkBlockTextError(text, maxCharLimit, foundBlackWords, voice);

  const getBlockErrorConfig = () => {
    const sanitizeOptions = {
      allowedTags: ['br', 'p', 'strong'],
      allowedAttributes: {
        p: ['style'],
        br: ['style'],
      },
    };

    if (exceedBlockMaxChar) {
      return {
        message: t('exceedBlockMaxChar', {
          maxCredits: maxCharLimit.toLocaleString(language),
        }),
        sanitizeOptions,
      };
    }
    if (hasInvalidCharacterLength) {
      return {
        message: t('invalidCharLengthMessage', {
          invalidTexts: formatInvalidTexts(invalidCharacterLengthTexts),
          maxCharacterLength: MAX_CHARACTER_LENGTH - 1,
        }),
        sanitizeOptions,
      };
    }
    if (hasInvalidCharacters) {
      return {
        message: t('invalidCharacter', { invalidText: text.trim() }),
        sanitizeOptions,
      };
    }
    if (hasBlackWords) {
      return {
        message: t('sentenceHasBlackWordsDescription'),
        sanitizeOptions,
      };
    }
    return { message: '', sanitizeOptions };
  };

  const isSynthesizing =
    isDownloading || status === CONVERTING_STATUS.IN_PROGRESS;
  // Converting this block's text to audio, or the whole project — AI narration
  // must be blocked while a conversion is running.
  const isConverting = status === CONVERTING_STATUS.IN_PROGRESS;
  const hasSuccessSynthesized =
    audioLink && status === CONVERTING_STATUS.SUCCESS;
  const isAudioExpired = audioExpiredAt && moment().isAfter(audioExpiredAt);
  const showReSynthesize =
    status === CONVERTING_STATUS.SUCCESS ||
    status === CONVERTING_STATUS.FAILURE;

  const costsCredit =
    (voice?.creditFactor || voiceDefault?.creditFactor) * countTextLength(text);
  const reSynthesizeTooltip = enableCreditUsagePreview
    ? t('costsCredit', { value: costsCredit?.toLocaleString(language) })
    : t('convertToAudioToListen');

  const isAINarration = hasNarrationConfig(source);
  const allTextsEmpty = useSelector((state) =>
    Object.values(state.project.multiBlock?.blocks || {}).every(
      (b) => !b?.text,
    ),
  );
  const isGeneratingThisBlock =
    isGeneratingNarration && generatingBlockIds?.includes(blockId);
  const showTextSkeleton =
    isGeneratingThisBlock ||
    isBlockTextSkeleton({
      block,
      isSkeletonUI,
      isAINarration,
      allTextsEmpty,
    });
  const showImageSkeleton = isSkeletonUI && !block?.previewImageUrl;
  const showConfigSkeleton = showImageSkeleton && showTextSkeleton;

  // While an AI-narration project is still preparing its text (extraction or
  // generation PENDING), the block text is not final yet — don't flag text
  // errors (max chars, invalid chars, black words) on transient content.
  const isProjectPending =
    source?.extraction?.response?.status === EXTRACTION_STATUS.PENDING ||
    source?.generation?.response?.status === EXTRACTION_STATUS.PENDING;
  const hasBlockError =
    !(isAINarration && isProjectPending) &&
    (exceedBlockMaxChar ||
      hasInvalidCharacterLength ||
      hasInvalidCharacters ||
      hasBlackWords);

  const isThisBlockActive = currentPlayingBlockId === blockId;
  const popoverCurrentTime = isThisBlockActive ? blockAudioCurrentTime : 0;
  const popoverDuration = isThisBlockActive ? blockAudioDuration : 0;

  const [content, setContent] = useState(
    EditorState.createEmpty(
      generateDecorator(BREAK_TIME_REGEX, BreakTimeHighlight),
    ),
  );
  const [openVoices, setOpenVoices] = useState(false);

  const [openPopperBlockOption, setOpenPopperBlockOption] = useState(false);

  const handleOpenVoices = () => {
    const voiceOwnershipFilter = filterVoice?.voiceOwnership;
    const isClonedVoice = isClonedVoiceCode(voice?.code);
    let voiceOwnership = isClonedVoice
      ? VOICE_OWNERSHIP.PERSONAL
      : VOICE_OWNERSHIP.VBEE;

    if (
      isClonedVoice &&
      (voiceOwnershipFilter === VOICE_OWNERSHIP.COMMUNITY ||
        voice?.userId !== userId)
    )
      voiceOwnership = VOICE_OWNERSHIP.COMMUNITY;

    if (!voiceOwnershipFilter || voiceOwnershipFilter !== voiceOwnership)
      dispatch(
        actions.voice.changeFilterVoice({
          ...filterVoice,
          voiceOwnership,
        }),
      );

    setOpenVoices(true);
  };
  const handleCloseVoices = () => setOpenVoices(false);

  const isCheckBlockChanged = useMemo(() => {
    const withoutExtension = block?.audioLink?.split('.')?.pop();
    const currentBlock = {
      text: block?.text,
      voiceCode: block?.voiceCode,
      speed: block?.speed,
      audioType: withoutExtension,
      emphasis: block?.emphasis,
    };
    const blockOriginal = {
      text: originalBlock?.text,
      voiceCode: originalBlock?.voiceCode,
      speed: originalBlock?.speed,
      audioType,
      emphasis: originalBlock?.emphasis,
    };

    return isBlockChanged(currentBlock, blockOriginal);
  }, [block, originalBlock, audioType]);

  const isAutoSaveOverLimit = useIsAutoSaveOverLimit();
  const isDisableConvertBtn = useMemo(
    () => !text || countTextLength(text) > maxCharLimit || isAutoSaveOverLimit,
    [
      text,
      audioLink,
      status,
      isCheckBlockChanged,
      isAudioExpired,
      isAutoSaveOverLimit,
    ],
  );

  const isDisablePlayAudio = useMemo(
    () => isCheckBlockChanged || !hasSuccessSynthesized || isAudioExpired,
    [isCheckBlockChanged, hasSuccessSynthesized, isAudioExpired],
  );

  const handleChangeContent = (newContent) => setContent(newContent);

  const handleChangeVoice = (newVoice) => {
    dispatch(
      actions.project.updateBlock(blockId, {
        hasEmphasis: newVoice?.hasEmphasis,
        emphasis: 0,
        voiceCode: newVoice?.code,
        blockStatus,
        voice: {
          code: newVoice?.code,
          name: newVoice?.name,
          roundImage: newVoice?.roundImage,
          language: {
            roundImage: newVoice?.language?.roundImage,
          },
          level: newVoice?.level,
          provider: newVoice?.provider,
          eolDate: newVoice?.eolDate,
          creditFactor: newVoice?.creditFactor,
          canonicalVoice: newVoice?.canonicalVoice,
          discardAt: newVoice?.discardAt,
          retentionDays: newVoice?.retentionDays,
          status: newVoice?.status,
          userId: newVoice?.userId,
          features: Array.isArray(newVoice?.features) ? newVoice.features : [],
          labels: newVoice?.labels,
        },
      }),
    );
  };

  const handleChangeSpeed = (field, value) => {
    dispatch(
      actions.project.updateBlock(blockId, { speed: value, blockStatus }),
    );
  };

  const handleChangeEmphasis = (field, value) => {
    dispatch(
      actions.project.updateBlock(blockId, { emphasis: value, blockStatus }),
    );
  };

  const handleChangeBreakTime = (value) => {
    const currentContent = content.getCurrentContent();
    const selection = content.getSelection();
    const label = `<break time=${value}s/>`;
    const textWithEntity = Modifier.insertText(
      currentContent,
      selection,
      label,
      OrderedSet.of('BOLD'),
    );

    dispatch(
      actions.project.updateBlock(blockId, {
        breakTime: value,
        text: textWithEntity.getPlainText(),
        blockStatus,
      }),
    );
    handleChangeContent(
      EditorState.push(content, textWithEntity, 'insert-characters'),
    );
  };

  const handleChangeText = (value) => {
    dispatch(
      actions.project.updateBlock(blockId, { text: value, blockStatus }),
    );
  };

  const handleEditorChange = (editorState) => {
    const selectionState = editorState.getSelection();
    if (!selectionState.toJS().hasFocus) return;
    const contentState = editorState.getCurrentContent();
    const plainText = contentState.getPlainText('\n');
    let newContent = editorState;

    const isContentChanged = !content
      .getCurrentContent()
      .equals(editorState.getCurrentContent());
    if (isContentChanged) {
      const currentStyle = editorState.getCurrentInlineStyle();
      if (currentStyle && currentStyle.size) {
        newContent = EditorState.setInlineStyleOverride(
          editorState,
          OrderedSet.of(),
        );
      }
      handleChangeContent(newContent);
      handleChangeText(plainText);
    } else {
      handleChangeContent(editorState);
    }
  };

  const handlePastedText = (txt) => {
    // Join all lines into a single text and insert at cursor position
    const joinedText = txt.replace(BREAK_LINE_DELIMITER, ' ');
    const selectionState = content.getSelection();
    const currentContent = content.getCurrentContent();

    const newContentState = Modifier.replaceText(
      currentContent,
      selectionState,
      joinedText,
    );

    const newEditorState = EditorState.push(
      content,
      newContentState,
      'insert-characters',
    );

    handleChangeContent(newEditorState);
    handleChangeText(newContentState.getPlainText());
    return true;
  };

  const handleFocusOnBlock = (e) => {
    onFocusingBlock(blockId);
    if (
      !blockRef.current?.contains(e?.target) ||
      toolbarWrapperRef.current?.contains(e?.target)
    ) {
      return;
    }
    inputRef.current?.focus();
  };

  const handleDownloadBlockAudio = async () => {
    if (isGeneratingNarration) return;
    const response = await getPresignedAudioUrl(requestId);
    if (response?.status) {
      const audioUrl = response.result?.audio;
      const audioName = getFileName(audioLink);
      downloadFileByUrl(audioUrl, `${indexBlock}_${audioName}`);
      trackBlockAudioDownloaded({ requestId });
    }
  };

  const handleOpenErrorReport = () => {
    const ticketMetadata = {
      requestId,
      voiceCode,
      userId,
      blockId,
      projectId,
    };
    const base64TicketMetadata = btoa(JSON.stringify(ticketMetadata));
    const encodedTicketMetadata = encodeURIComponent(base64TicketMetadata);

    window.open(
      `${LANDING_PAGE_URL}/support?metadata=${encodedTicketMetadata}&platform=${PLATFORM_TYPE.DESKTOP_WEB}&category=${CATEGORY_TYPE.PROJECT}`,
      '_blank',
    );
  };

  useEffect(() => {
    if (text) {
      const contentEditor = EditorState.createWithContent(
        ContentState.createFromText(text),
        generateDecorator(BREAK_TIME_REGEX, BreakTimeHighlight),
      );
      handleChangeContent(contentEditor);
    }
  }, []);

  useEffect(() => {
    setContent((prev) => {
      const currentPlainText = prev.getCurrentContent().getPlainText('\n');
      if (currentPlainText === (text || '')) return prev;
      return EditorState.createWithContent(
        ContentState.createFromText(text || ''),
        prev.getDecorator() ||
          generateDecorator(BREAK_TIME_REGEX, BreakTimeHighlight),
      );
    });
  }, [text]);

  useEffect(() => {
    if (isFocusing) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isFocusing]);

  useEffect(() => {
    dispatch(actions.project.updateBlockStatus({ blockId, blockStatus }));
  }, [blockStatus]);

  useEffect(() => {
    const currentBrowser = checkBrowser();
    const decorators = [
      { regex: BREAK_TIME_REGEX, component: BreakTimeHighlight },
    ];

    if (foundBlackWords.length > 0) {
      const blackWordsRegex = foundBlackWords.map((bw) =>
        bw.replaceAll(' ', ' +'),
      );
      const blackWordPattern = !currentBrowser?.safariAgent
        ? new RegExp(
            `(?<![${VALID_LETTERS}])(${blackWordsRegex.join(
              '|',
            )})(?![${VALID_LETTERS}])`,
            'gi',
          )
        : new RegExp(
            `(^|[^${VALID_LETTERS}])(${blackWordsRegex.join(
              '|',
            )})([^${VALID_LETTERS}]|$)`,
            'gi',
          );
      decorators.push({
        regex: blackWordPattern,
        component: BlackWordHighLight,
      });
    }

    setContent((prev) =>
      EditorState.set(prev, {
        decorator: generateCompositeDecorator(decorators),
      }),
    );
  }, [foundBlackWords.join(',')]);

  const selectedBlockIds = useSelector(
    (state) => state.project.selectedBlockIds,
  );

  const isSelected = useMemo(
    () => selectedBlockIds.includes(blockId),
    [selectedBlockIds, blockId],
  );

  const renderConfigBlock = () => (
    <ConfigOptionBlock
      handleOpenVoices={handleOpenVoices}
      handleChangeSpeed={handleChangeSpeed}
      handleChangeBreakTime={handleChangeBreakTime}
      handleChangeEmphasis={handleChangeEmphasis}
      voice={voice}
      speed={speed}
      breakTime={breakTime}
      hasEmphasis={hasEmphasis}
      emphasis={emphasis}
      // Disable every block config (voice/speed/break/emphasis) while the source
      // is still extracting — any edit here would be overwritten by the data
      // streamed back from the BE, so it must not be editable during loading.
      isSynthesizing={isSynthesizing || isSkeletonUI}
      isBlockActive={isFocusing || isSelected}
      isBlockUIImprovement
      isGeneratingBlock={showTextSkeleton}
    />
  );
  const renderCharacterCount = () => (
    <CreditsCount
      creditFactor={voice?.creditFactor}
      exceedBlockMaxCredits={exceedBlockMaxChar}
      credits={countTextLength(text)}
      maxCredits={maxCharLimit}
    />
  );

  const renderSlideThumbnail = () => {
    if (block?.previewImageUrl) {
      return (
        <div className="slide-thumbnail">
          <img src={block.previewImageUrl} alt={`Slide ${indexBlock}`} />
        </div>
      );
    }
    if (showImageSkeleton) return <SlideThumbnailSkeleton />;
    return <div className="slide-thumbnail-placeholder" />;
  };

  const handleSelectBlock = () => {
    if (isSelected) {
      dispatch(actions.project.unselectBlocks([blockId]));
      return;
    }
    dispatch(actions.project.selectBlocks([blockId]));
  };

  const renderSelectBlock = () => (
    <>
      <div className="select-block select-block-new-ui-v2">
        <Checkbox
          checked={isSelected}
          onChange={handleSelectBlock}
          className={`checkbox-color ${
            isSkeletonUI || isSynthesizing ? 'disabled-btn' : ''
          }`}
          disabled={isSkeletonUI || isSynthesizing}
        />
      </div>
    </>
  );

  const getBlockBorderColor = ({ isError, isActive }) => {
    if (isError) return COLOR.functionalRed.default;
    if (isActive) return COLOR.indigo.default;
    return COLOR.label['16'];
  };

  return (
    <StyledContentBlockWrapper isLastBlock={isLastBlock}>
      <StyledContentBlock
        isLastParagraphId={isLastBlock}
        isFocusingOnBlock={isFocusing || isSelected}
        isError={hasBlockError}
        isHighlight={isHighlight}
      >
        <StyledOrdinalNumberBlock>
          {/* Time Frame Row for Dubbing Projects */}

          <StyledInfoBlock>
            <NewTypography
              color={COLOR.descriptionUpload}
              variant="body-14-regular"
            >
              {indexBlock}.
            </NewTypography>

            <EditNameBlock blockId={blockId} title={title} index={indexBlock} />
          </StyledInfoBlock>

          <div
            className="block-wrapper block-wrapper-v2"
            ref={blockRef}
            onClick={handleFocusOnBlock}
            role="button"
            tabIndex="-1"
            style={{
              border: `0.5px solid ${getBlockBorderColor({
                isError: hasBlockError,
                isActive: isHighlight || isSelected,
              })}`,
            }}
          >
            <div
              ref={toolbarWrapperRef}
              className="block-toolbar-wrapper"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: 0,
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flex: 1,
                  gap: '8px',
                }}
              >
                {renderSelectBlock()}
                <div className="block-header-divider" />
                {showConfigSkeleton ? (
                  <ConfigToolbarSkeleton />
                ) : (
                  renderConfigBlock()
                )}
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  flexShrink: 0,
                }}
              >
                {showConfigSkeleton && <BlockActionsSkeleton />}
                {!showConfigSkeleton &&
                  slideV2Enabled &&
                  hasNarrationConfig(source) &&
                  isNoteChanged(noteChangedBlock) &&
                  !isGeneratingThisBlock &&
                  !isConverting && (
                    <NoteChangedWarningPopover
                      blockId={blockId}
                      onOpenAIGenerateDialog={onOpenAIGenerateDialog}
                      disabled={isDownloading}
                    />
                  )}
                {!showConfigSkeleton && hasBlockError && (
                  <Tooltip
                    arrow
                    title={
                      <Typography
                        component="div"
                        sx={{
                          fontSize: '14px',
                          textAlign: 'left',
                          display: 'block',
                        }}
                        dangerouslySetInnerHTML={{
                          __html: (() => {
                            const { message, sanitizeOptions } =
                              getBlockErrorConfig();
                            return sanitize(message, sanitizeOptions);
                          })(),
                        }}
                      />
                    }
                    placement="left"
                  >
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      className="exceed-block-char-icon"
                    >
                      <WarningRoundedIcon
                        sx={{ color: COLOR.danger, fontSize: '24px' }}
                      />
                    </div>
                  </Tooltip>
                )}
                {!showConfigSkeleton && (
                  <>
                    <NewSynthesisButton
                      blockId={blockId}
                      status={status}
                      isPlaying={isPlayingBlock}
                      isDisableConvertBtn={
                        isDisableConvertBtn || isSkeletonUI || isDownloading
                      }
                      onSaveProject={onSaveProject}
                      showSuccessIcon={showSuccessIcon}
                      audioExpiredAt={audioExpiredAt}
                      onOpenOutOfCreditsDialog={onOpenOutOfCreditsDialog}
                      onOpenWrongSyntaxDialog={onOpenWrongSyntaxDialog}
                      onOpenWrongSyntaxDialogV2={onOpenWrongSyntaxDialogV2}
                      checkValidCreateSynthesis={checkValidCreateSynthesis}
                      handlePlayAudio={handlePlayAudio}
                      onOpenDiscardedVoiceCloningDialog={
                        onOpenDiscardedVoiceCloningDialog
                      }
                      isDisablePlayAudio={isDisablePlayAudio || isSkeletonUI}
                      onConvertBlockToAudio={onConvertBlockToAudio}
                      isBlockEditting={isBlockEditting}
                      enableCreditUsagePreview={enableCreditUsagePreview}
                      isBlockUIImprovement
                      showAudioPopover={showReSynthesize}
                      audioLink={audioLink}
                      popoverCurrentTime={popoverCurrentTime}
                      popoverDuration={popoverDuration}
                      onSeekInBlock={handleSeekedInBlock}
                    />
                    <MoreOptionBlock
                      openMoreOptionBlock={openPopperBlockOption}
                      onOpenMoreOptionBlock={setOpenPopperBlockOption}
                      onOpenReportDialog={handleOpenErrorReport}
                      isDisabledDownloadBlockAudio={
                        isDisablePlayAudio ||
                        isGeneratingNarration ||
                        isSkeletonUI
                      }
                      onDownloadBlockAudio={handleDownloadBlockAudio}
                      onReSynthesizeBlock={() => onReSynthesizeBlock(blockId)}
                      showReSynthesize={showReSynthesize}
                      isDisabledReSynthesize={isDownloading}
                      reSynthesizeTooltip={reSynthesizeTooltip}
                      hideDeleteBlock
                    />
                  </>
                )}
              </div>
            </div>

            {/* Text editor */}
            <div className="editor-wrapper editor-wrapper-new-ui">
              {renderSlideThumbnail()}
              <div className="text-column">
                <StyledBlockEditor
                  onClick={handleFocusOnBlock}
                  role="button"
                  tabIndex="-1"
                  isFocusingOnBlock={isFocusing}
                >
                  <div className="editor-container">
                    {showTextSkeleton ? (
                      <TextLinesSkeleton />
                    ) : (
                      <Editor
                        placeholder={t('enterTextHere')}
                        editorState={content}
                        handlePastedText={handlePastedText}
                        stripPastedStyles
                        onChange={handleEditorChange}
                        ref={inputRef}
                        // Read-only while extracting — streamed text would
                        // overwrite any edit made during loading.
                        readOnly={isSynthesizing || isSkeletonUI}
                      />
                    )}
                  </div>
                </StyledBlockEditor>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  {!showTextSkeleton && renderCharacterCount()}
                </div>
              </div>
            </div>
          </div>
        </StyledOrdinalNumberBlock>
        {openVoices && (
          <VoiceSelectionDialog
            open={openVoices}
            activeVoiceId={voice && voice.id}
            activeVoiceCode={voice && voice.code}
            voiceStatus={voice && voice.status}
            onClose={handleCloseVoices}
            onChangeVoice={handleChangeVoice}
            hasLanguageFilter
          />
        )}
      </StyledContentBlock>
    </StyledContentBlockWrapper>
  );
};

function arePropsEqual(prevProps, nextProps) {
  return (
    prevProps.blockId === nextProps.blockId &&
    prevProps.isLastBlock === nextProps.isLastBlock &&
    prevProps.isFocusing === nextProps.isFocusing &&
    prevProps.block === nextProps.block &&
    prevProps.voiceDefault === nextProps.voiceDefault &&
    prevProps.isDownloading === nextProps.isDownloading &&
    prevProps.isHighlight === nextProps.isHighlight &&
    prevProps.projectId === nextProps.projectId &&
    prevProps.isPlayingBlock === nextProps.isPlayingBlock &&
    prevProps.originalBlock === nextProps.originalBlock &&
    prevProps.audioType === nextProps.audioType &&
    prevProps.blockAudioCurrentTime === nextProps.blockAudioCurrentTime &&
    prevProps.blockAudioDuration === nextProps.blockAudioDuration &&
    prevProps.currentPlayingBlockId === nextProps.currentPlayingBlockId &&
    prevProps.onOpenAIGenerateDialog === nextProps.onOpenAIGenerateDialog &&
    prevProps.foundBlackWords?.join(',') ===
      nextProps.foundBlackWords?.join(',')
  );
}

export default memo(ContentBlock, arePropsEqual);
